import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;


function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function parseApiResponse(raw) {
  let d = parseJsonIfString(raw);
  if (d?.body != null) return parseJsonIfString(d.body);
  return d;
}

function isApiSuccess(res) {
  if (!res || typeof res !== "object") return false;
  const s = res.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function pickGiFromResponse(res) {
  const block = parseJsonIfString(res?.data != null ? res.data : res);
  if (!block || typeof block !== "object" || Array.isArray(block)) return null;
  if (block.goodsInwards && typeof block.goodsInwards === "object") {
    return block.goodsInwards;
  }
  if (block._id != null || block.id != null || block.giNumber != null) {
    return block;
  }
  return null;
}

function displayValue(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function displayQty(value) {
  if (value == null || value === "") return "-";
  return value;
}

function formatDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, "/");
  const m = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/);
  if (m) return `${m[1]}/${m[2]}/${m[3]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("day")}/${get("month")}/${get("year")}`;
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}


const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
);

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
);


function GiSupplierView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gi, setGi] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchGi = useCallback(async () => {
    if (!id) {
      toast.error("Valid id is required", { autoClose: 2500 });
      navigate("/giSupplierList");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-goods-inwards-supplier-id",
        currentUserId: localStorage.getItem("id") || undefined,
        giId: id,
      });
      const res = parseApiResponse(response.data);

      if (!isApiSuccess(res)) {
        toast.error(res?.message || "Failed to fetch goods inwards", {
          autoClose: 3000,
        });
        navigate("/giSupplierList");
        return;
      }

      const record = pickGiFromResponse(res);
      if (!record) {
        toast.error("Goods inwards not found", { autoClose: 3000 });
        navigate("/giSupplierList");
        return;
      }
      setGi(record);
    } catch (error) {
      console.error("Fetch supplier GI error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "Failed to fetch goods inwards",
        { autoClose: 3000 },
      );
      navigate("/giSupplierList");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchGi();
  }, [fetchGi]);

  const materials = useMemo(
    () => (Array.isArray(gi?.materials) ? gi.materials : []),
    [gi],
  );

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View Goods Inwards</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to={"/giSupplierList"}>
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

  const wrapCard = (body) => (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">{body}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return wrapCard(
      <div className="card-body text-center py-5">
        <PulseLoader color="#1199B2" size={10} />
      </div>,
    );
  }

  if (!gi) {
    return wrapCard(
      <div className="card-body text-center py-5">
        <p>Goods inwards not found</p>
      </div>,
    );
  }

  const cell = { padding: "4px 2px", whiteSpace: "nowrap" };
  const cellWrap = {
    padding: "4px 2px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  };

  return wrapCard(
    <div className="card-body">
      <SectionTitle icon="fa-file-lines" title="GI Details" />
      <ViewField label="GI Number">{displayValue(gi.giNumber)}</ViewField>
      <ViewField label="GI Date">{formatDate(gi.giDate)}</ViewField>
      <ViewField label="PO Number">{displayValue(gi.poNumber)}</ViewField>
      <ViewField label="PO Date">{formatDate(gi.poDate)}</ViewField>
      <ViewField label="Invoice Number">
        {displayValue(gi.invoiceNumber)}
      </ViewField>
      <ViewField label="Invoice Date">{formatDate(gi.invoiceDate)}</ViewField>

      <SectionTitle icon="fa-boxes-stacked" title="Materials" />
      {materials.length === 0 ? (
        <div className="txtdsply text-muted mb-3">No materials</div>
      ) : (
        <div className="mb-3" style={{ overflowX: "hidden" }}>
          <table
            className="table table-bordered table-sm mb-0"
            style={{ width: "100%", tableLayout: "fixed", fontSize: "11px" }}
          >
            <thead>
              <tr style={{ backgroundColor: "#003366", color: "#fff" }}>
                <th style={{ width: "4%", padding: "4px 2px" }}>S.No</th>
                <th style={{ width: "11%", padding: "4px 2px" }}>Material Code</th>
                <th style={{ width: "18%", padding: "4px 2px" }}>
                  Material Description
                </th>
                <th style={{ width: "8%", padding: "4px 2px" }}>HSN</th>
                <th style={{ width: "5%", padding: "4px 2px" }}>UOM</th>
                <th style={{ width: "7%", padding: "4px 2px" }}>PO Qty</th>
                <th style={{ width: "7%", padding: "4px 2px" }}>Inward</th>
                <th style={{ width: "7%", padding: "4px 2px" }}>Accepted</th>
                <th style={{ width: "7%", padding: "4px 2px" }}>Rejected</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((row, index) => (
                <tr key={row._id || row.id || index}>
                  <td style={cell}>{index + 1}</td>
                  <td style={cellWrap}>{displayValue(row.materialCode)}</td>
                  <td style={cellWrap}>{displayValue(row.materialDescription)}</td>
                  <td style={cellWrap}>{displayValue(row.hsnCode)}</td>
                  <td style={cell}>{displayValue(row.uom)}</td>
                  <td style={cell}>{displayQty(row.poQuantity)}</td>
                  <td style={cell}>{displayQty(row.inwardQuantity)}</td>
                  <td style={cell}>{displayQty(row.acceptedQuantity)}</td>
                  <td style={cell}>{displayQty(row.rejectedQuantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>,
  );
}

export default GiSupplierView;